import { useEffect, useRef, useState, type ReactNode } from "react";

interface Props {
  /** Chip face content (the .dt-when text, clear button, …). */
  chip: ReactNode;
  /** Extra classes on the chip face (e.g. "is-disabled"). */
  chipClass?: string;
  /** Base chip class — "dt-chip" (default) or "date-chip" (repeat panel). */
  chipBase?: string;
  disabled?: boolean;
  /** Anchor the popover to the chip's right edge (for chips near the card edge). */
  align?: "left" | "right";
  ariaLabel?: string;
  /** Controlled open state (optional — uncontrolled by default). Callers that
   *  open at a specific picker stage (clicking the chip's time part) own it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Popover content; call `close` after a final pick. */
  children: (close: () => void) => ReactNode;
}

/** A paper chip that opens a floating picker beneath it. Closes on outside
 *  click and Escape (the dialog's own Escape handler skips while a `.chip-pop`
 *  is open, so Escape dismisses the picker, not the whole form). */
export function ChipPopover({ chip, chipClass, chipBase = "dt-chip", disabled, align, ariaLabel, open: openProp, onOpenChange, children }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp !== undefined ? openProp : internalOpen;
  const setOpen = (v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    if (openProp === undefined) setInternalOpen(v);
  };
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div className="dt-chip-wrap" ref={ref}>
      <div
        className={[chipBase, chipClass, open ? "is-open" : ""].filter(Boolean).join(" ")}
        onClick={() => { if (!disabled) setOpen(!open); }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(!open); }
        }}
      >
        {chip}
      </div>
      {open && (
        <div className={"chip-pop" + (align === "right" ? " align-right" : "")}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
